/**
 * Vocabulario de roles para la interfaz.
 *
 * La fuente de verdad de los permisos es `backend/src/constants/permissions.js`.
 * Aquí solo vive cómo se NOMBRAN los roles y cuáles puede asignar cada quien.
 */

export const ROLES = {
  SUPER_ADMIN: 'super-admin',
  ADMIN: 'admin',
  SEC: 'sec',
  BASIC: 'Basic',
  DIRECTOR: 'director',
};

/** `Basic` conserva la mayúscula: es el valor real en base de datos. */
export const ROLE_LABELS = {
  [ROLES.SUPER_ADMIN]: 'Super Administrador',
  [ROLES.ADMIN]: 'Administrador',
  [ROLES.SEC]: 'Secretario de Zona',
  [ROLES.BASIC]: 'Juez',
  [ROLES.DIRECTOR]: 'Director',
};

export const roleLabel = role => ROLE_LABELS[role] || role || '—';

/** Roles que pueden entrar por el portal web. El director usa el suyo. */
export const WEB_ROLES = [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.SEC];

/** El rol `sec` no significa nada sin zona: su alcance sería vacío. */
export const ROLES_REQUIRING_ZONE = [ROLES.SEC];

/**
 * Roles que el usuario actual puede asignar.
 *
 * Crear o promover administradores exige `users.grant-admin`, que `admin` no tiene:
 * un administrador no puede fabricar más administradores.
 *
 * `super-admin` NO aparece nunca, ni para quien tiene `users.grant-admin`: es la
 * cuenta de recuperación y solo se otorga con `scripts/create-super-admin.js`, con
 * acceso a la base de datos. El servidor rechaza el rol con 403 aunque se pida a mano
 * (`ASSIGNABLE_ROLES` en `backend/src/constants/permissions.js`), así que esto no es
 * un maquillaje de la interfaz: las dos puertas dicen lo mismo.
 */
export const assignableRoles = hasPermission => {
  const base = [ROLES.BASIC, ROLES.SEC];
  if (hasPermission('users.grant-admin')) base.push(ROLES.ADMIN);
  return base;
};

/** `admin` y `super-admin`: lo que el backend llama `ADMIN_ROLES`. */
export const isAdministrativeRole = role => role === ROLES.ADMIN || role === ROLES.SUPER_ADMIN;

/**
 * Las tres funciones siguientes reproducen las reglas que el servidor ya aplica.
 * No son la autorización — son para no ofrecer botones que van a devolver 403 o 404.
 * Si una deja de coincidir con el backend, la que manda es la del backend.
 */

/** `updateRole`: promover o degradar cuentas administrativas exige `users.grant-admin`. */
export const canChangeRole = ({ actor, hasPermission, target }) => {
  if (!hasPermission('users.change-role')) return false;
  // Un super-admin solo lo toca otro super-admin: es la cuenta de recuperación.
  if (target?.role === ROLES.SUPER_ADMIN) return actor?.role === ROLES.SUPER_ADMIN;
  if (isAdministrativeRole(target?.role)) return hasPermission('users.grant-admin');
  return true;
};

/**
 * `adminResetPassword`: sobre una cuenta administrativa responde 404 (no 403) sin
 * `users.reset-admin-password`, para no confirmar qué cuentas lo son. Aquí el botón
 * simplemente no se pinta.
 */
export const canResetPassword = ({ hasPermission, target }) => {
  if (!hasPermission('users.reset-password')) return false;
  if (isAdministrativeRole(target?.role)) return hasPermission('users.reset-admin-password');
  return true;
};

/** `updateUserPermissions`: al super-admin los overrides no le hacen nada (400). */
export const canEditPermissions = ({ hasPermission, target }) =>
  hasPermission('permissions.manage') && target?.role !== ROLES.SUPER_ADMIN;
